import * as Y from 'yjs';

import type { Block, Document, Fragment, Inline } from '@gitbook/slate';

export type YSlateData = Y.Map<YValue>;

export type YSlateAbstractNode = Y.XmlElement<{
    key: string;
    data: YSlateData;
}>;

export type YSlateDocument = YSlateAbstractNode &
    Y.XmlElement<{
        key: Document['key'];
        data: YSlateData;
    }>;
export type YSlateInline = YSlateAbstractNode &
    Y.XmlElement<{
        key: Inline['key'];
        isVoid: Inline['isVoid'];
        type: Inline['type'];
        data: YSlateData;
        fragments: YSlateFragments;
    }>;
export type YSlateBlock = YSlateAbstractNode &
    Y.XmlElement<{
        key: Block['key'];
        isVoid: Block['isVoid'];
        type: Block['type'];
        data: YSlateData;
        fragments: YSlateFragments;
    }>;
export type YSlateFragment = YSlateAbstractNode &
    Y.XmlElement<{
        key: Fragment['key'];
        fragment?: Fragment['fragment'];
        type?: Fragment['type'];
    }>;
export type YSlateFragments = Y.XmlFragment;
export type YSlateText = Y.XmlText;
export type YSlateNode = YSlateText | YSlateInline | YSlateBlock | YSlateFragment;

type Primative = null | undefined | number | string | boolean;
export type JSValue =
    | Primative
    | Array<JSValue>
    | { [key: string]: JSValue }
    | Map<string, JSValue>;
export type YValue = Primative | Y.Array<YValue> | Y.Map<YValue>;

/**
 * Get the data slate document from a Y.Doc.
 */
export function getYSlateDocument(doc: Y.Doc): YSlateDocument {
    return doc.get('data', Y.XmlElement);
}

export function isYSlateBlock(node: YSlateNode): node is YSlateBlock {
    return node instanceof Y.XmlElement && node.nodeName === 'block';
}

export function isYSlateFragment(node: YSlateNode): node is YSlateFragment {
    return node instanceof Y.XmlElement && node.nodeName === 'fragment';
}

export function isYSlateText(node: YSlateNode): node is YSlateText {
    return node instanceof Y.XmlText;
}

export function isYSlateInline(node: YSlateNode): node is YSlateInline {
    return node instanceof Y.XmlElement && node.nodeName === 'inline';
}

export function assertYSlateBlock(node: YSlateNode): asserts node is YSlateBlock {
    if (!isYSlateBlock(node)) {
        throw new Error('Expected a YSlateBlock');
    }
}

export function assertYSlateFragment(node: YSlateNode): asserts node is YSlateFragment {
    if (!isYSlateFragment(node)) {
        throw new Error('Expected a YSlateFragment');
    }
}

export function assertYSlateText(node: YSlateNode): asserts node is YSlateText {
    if (!isYSlateText(node)) {
        throw new Error('Expected a YSlateText');
    }
}

export function assertYSlateInline(node: YSlateNode): asserts node is YSlateInline {
    if (!isYSlateInline(node)) {
        throw new Error('Expected a YSlateInline');
    }
}
